import { useCallback, useState, useEffect } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
}

/**
 * Tour hook for Sale Process Board
 * Uses shared tour-styles for consistent styling
 */
export const useSaleProcessTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [_currentTourStep, setCurrentTourStep] = useState(0)

    const tourSteps: TourStep[] = [
        {
            selector: '[data-tour="add-journey-btn"]',
            title: 'Thêm Quá Trình Mới',
            description:
                'Bạn muốn tạo quá trình bán hàng mới? Click vào đây để thêm quá trình (Journey) hoặc Upsell cho khách hàng.',
        },
        {
            selector: '[data-tour="sale-process-board"]',
            title: 'Bảng Quy Trình Bán Hàng',
            description:
                'Đây là bảng theo dõi quy trình bán hàng với 4 giai đoạn: Trao đổi, Meeting, POC và Hợp đồng.',
        },
        {
            selector: '[data-tour="journey-header"]',
            title: 'Thông Tin Hành Trình',
            description:
                'Hiển thị số thứ tự hành trình, trạng thái (Upsell/Won/On Hold), ngày tạo và ngày dự kiến chốt deal.',
        },
        {
            selector: '[data-tour="sales-followers"]',
            title: 'Sales Phụ Trách',
            description:
                'Danh sách Sales đang phụ trách hành trình này. Sales đầu tiên trong danh sách là Sales chính.',
        },
        {
            selector: '[data-tour="service-interestings"]',
            title: 'Dịch Vụ Quan Tâm',
            description:
                'Chọn các dịch vụ khách hàng đang quan tâm. Thông tin này sẽ tự động lưu khi thay đổi.',
        },
        {
            selector: '[data-tour="edit-journey-info-btn"]',
            title: 'Chỉnh Sửa Thông Tin',
            description:
                'Click để mở panel chỉnh sửa thông tin hành trình: ngày dự kiến chốt, ghi chú, và thêm/bớt Sales phụ trách.',
        },
        {
            selector: '[data-tour="step-searching"]',
            title: 'Bước 1: Trao Đổi, Tìm Hiểu',
            description:
                'Giai đoạn đầu tiên - trao đổi và tìm hiểu nhu cầu khách hàng. Thêm ghi chú để theo dõi các cuộc trao đổi.',
        },
        {
            selector: '[data-tour="step-presentation"]',
            title: 'Bước 2: Meeting/Presentation',
            description:
                'Giai đoạn gặp gỡ khách hàng. Tạo phiếu công tác để gặp trực tiếp hoặc tạo ticket meeting online.',
        },
        {
            selector: '[data-tour="create-business-trip-btn"]',
            title: 'Tạo Phiếu Công Tác',
            description:
                'Tạo phiếu công tác để đi gặp khách hàng trực tiếp. Sau khi tạo, phiếu sẽ hiển thị trong mục Business Trips.',
        },
        {
            selector: '[data-tour="create-meeting-btn"]',
            title: 'Tạo Meeting Online',
            description:
                'Tạo ticket meeting online (Google Meet, Zoom...) để họp với khách hàng từ xa.',
        },
        {
            selector: '[data-tour="step-poc"]',
            title: 'Bước 3: Tạo POC',
            description:
                'Giai đoạn cho khách hàng dùng thử dịch vụ (Proof of Concept).',
        },
        {
            selector: '[data-tour="create-poc-btn"]',
            title: 'Tạo Dịch Vụ POC',
            description:
                'Tạo dịch vụ dùng thử cho khách hàng. Sau khi POC kết thúc, cập nhật kết quả để chuyển sang giai đoạn Hợp đồng.',
        },
        {
            selector: '[data-tour="step-contract"]',
            title: 'Bước 4: Hợp Đồng & Dịch Vụ',
            description:
                'Giai đoạn cuối - thương lượng giá, ký hợp đồng và kích hoạt dịch vụ chính thức.',
        },
        {
            selector: '[data-tour="add-negotiation-btn"]',
            title: 'Thêm Thương Lượng',
            description:
                'Ghi nhận các lần thương lượng giá với khách hàng. Bao gồm giá đề xuất, giá khách hàng yêu cầu, và ghi chú.',
        },
        {
            selector: '[data-tour="create-contract-btn"]',
            title: 'Tạo Hợp Đồng',
            description:
                'Tạo hợp đồng mới cho khách hàng. Sau khi hợp đồng được ký, hành trình sẽ tự động chuyển sang trạng thái Won.',
        },
        {
            selector: '[data-tour="add-note-btn"]',
            title: 'Thêm Ghi Chú',
            description:
                'Ghi nhận nội dung trao đổi, follow up với khách hàng. Ghi chú sẽ hiển thị theo từng bước trong quy trình.',
        },
        {
            selector: '[data-tour="journey-actions"]',
            title: 'Menu Thao Tác',
            description:
                'Menu nhanh: Đánh dấu On-hold khi deal tạm dừng hoặc không tiến triển.',
        },
    ]

    const showTourUI = (step: TourStep, stepIndex: number) => {
        injectTourStyles()
        cleanupTourElements()
        createTourOverlay()

        const element = document.querySelector(step.selector) as HTMLElement
        if (!element) {
            // Skip to next step if element not found
            if (stepIndex < tourSteps.length - 1) {
                goToStep(stepIndex + 1)
            } else {
                endTour()
            }
            return
        }

        element.classList.add('tour-highlight-element')
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // Wait for scroll then add popover
        setTimeout(() => {
            const popover = createTourPopover({
                title: step.title,
                description: step.description,
                currentStep: stepIndex + 1,
                totalSteps: tourSteps.length,
                onPrev:
                    stepIndex > 0 ? () => goToStep(stepIndex - 1) : undefined,
                onNext: () => {
                    if (stepIndex === tourSteps.length - 1) {
                        endTour()
                    } else {
                        goToStep(stepIndex + 1)
                    }
                },
                onClose: endTour,
                isLastStep: stepIndex === tourSteps.length - 1,
            })

            document.body.appendChild(popover)
            positionPopover(popover, element)
        }, 300)
    }

    const goToStep = (stepIndex: number) => {
        const step = tourSteps[stepIndex]
        if (!step) return

        cleanupTourElements()
        showTourUI(step, stepIndex)
        setCurrentTourStep(stepIndex)
    }

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentTourStep(0)
    }, [])

    const startTour = useCallback(() => {
        setIsActive(true)
        setCurrentTourStep(0)

        window.scrollTo({ top: 0, behavior: 'smooth' })

        setTimeout(() => {
            showTourUI(tourSteps[0], 0)
        }, 500)
    }, [])

    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return { startTour, endTour, isActive }
}

export default useSaleProcessTour
